/**
 * BOM Table Component
 * Displays extracted BOM items in a table matching Excel ground truth format
 */

import React, { useMemo, useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Typography,
  Box,
  Tooltip,
  TextField,
  InputAdornment,
  Link,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { 
  AutoAwesome as EnrichedIcon, 
  Search as SearchIcon, 
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Note as NoteIcon,
} from '@mui/icons-material';
import { BOMItem } from '../types/bom.types';
import { useBOM } from '../contexts/BOMContext';
import * as bomApi from '../services/bomApi';

interface BOMTableProps {
  items: BOMItem[];
  onItemSelect?: (item: BOMItem | null) => void;
}

const BOMTable: React.FC<BOMTableProps> = ({ items, onItemSelect }) => {
  const { excelGroundTruth, setCurrentPage, setExtractedItems, showToast, uploadedPdfFilename } = useBOM();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [verificationReportDialog, setVerificationReportDialog] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  // State for editable items
  const [editedItems, setEditedItems] = useState<Map<number, Partial<BOMItem>>>(new Map());
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; field: string } | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // State for notes dialog (PDF-level notes)
  const [notesDialog, setNotesDialog] = useState<{ open: boolean; notes: string }>({
    open: false,
    notes: ''
  });
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [jobNotes, setJobNotes] = useState<string | null>(null);

  // Reset to first page when search query changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  // Load job notes when PDF filename changes
  useEffect(() => {
    const loadJobNotes = async () => {
      if (uploadedPdfFilename) {
        try {
          const result = await bomApi.getJobNotes(uploadedPdfFilename);
          setJobNotes(result.notes);
        } catch (error: any) {
          console.error('Failed to load job notes:', error);
        }
      } else {
        setJobNotes(null);
      }
    };
    loadJobNotes();
  }, [uploadedPdfFilename]);
  
  // Handle verification - allows toggling between verified/incorrect/unverified
  const handleVerifyItem = async (item: BOMItem, status: 'verified' | 'incorrect', event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click/PDF navigation
    
    try {
      const extractionId = (item as any).extraction_id || (item as any).id;
      if (!extractionId) {
        showToast('error', 'Cannot verify: Item ID not found');
        return;
      }
      
      // Toggle: if clicking the same status, set to unverified
      const currentStatus = item.verification_status || 'unverified';
      const newStatus = currentStatus === status ? 'unverified' : status;
      
      const response = await fetch('/api/verify/item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          extraction_id: extractionId,
          verification_status: newStatus,
          verified_by: 'user'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to verify item');
      }
      
      // Update local state
      const updatedItems = items.map(i => {
        const itemId = (i as any).extraction_id || (i as any).id;
        if (itemId === extractionId) {
          return { ...i, verification_status: newStatus } as BOMItem;
        }
        return i;
      });
      
      setExtractedItems(updatedItems);
      
      const statusMessages = {
        'verified': 'Item verified ✓',
        'incorrect': 'Marked as incorrect ✗',
        'unverified': 'Verification removed'
      };
      showToast('success', statusMessages[newStatus] || 'Verification updated');
    } catch (error: any) {
      showToast('error', `Verification failed: ${error.message}`);
    }
  };
  
  // Handle PDF navigation
  const handleNavigateToPage = (page: number) => {
    if (page && page > 0) {
      setCurrentPage(page);
      // Scroll to PDF viewer if it exists
      const pdfViewer = document.querySelector('[data-pdf-viewer]');
      if (pdfViewer) {
        pdfViewer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Handle Complete Verification
  const handleCompleteVerification = async () => {
    if (!uploadedPdfFilename) {
      showToast('error', 'No PDF file loaded');
      return;
    }

    setIsGeneratingReport(true);
    try {
      // Generate verification report
      const response = await fetch('/api/verify/generate-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pdf_filename: uploadedPdfFilename,
          notes: verificationNotes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const result = await response.json();
      const report = result.report;
      
      // Save report
      const saveResponse = await fetch('/api/verify/save-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pdf_filename: uploadedPdfFilename,
          report: report
        })
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save report');
      }

      showToast('success', 'Verification report generated and saved successfully!');
      setVerificationReportDialog(false);
      setVerificationNotes('');
      // Show email dialog
      setEmailDialog(true);
    } catch (error: any) {
      showToast('error', `Failed to complete verification: ${error.message}`);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Handle Email Report
  const handleEmailReport = async () => {
    if (!uploadedPdfFilename) {
      showToast('error', 'No PDF file loaded');
      return;
    }

    if (!emailAddress || !emailAddress.includes('@')) {
      showToast('error', 'Please enter a valid email address');
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await fetch('/api/verify/email-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pdf_filename: uploadedPdfFilename,
          email: emailAddress
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      showToast('success', 'Verification report sent via email!');
      setEmailDialog(false);
      setEmailAddress('');
    } catch (error: any) {
      showToast('error', `Failed to send email: ${error.message}`);
    } finally {
      setIsSendingEmail(false);
    }
  };
  
  // Helper function to normalize text for comparison
  const normalizeText = (text: string | undefined | null): string => {
    if (!text) return '';
    return String(text).toLowerCase().trim().replace(/\s+/g, ' ');
  };
  
  // Find ground truth match for an item
  const findGroundTruthMatch = useMemo(() => {
    return (item: BOMItem) => {
      if (!excelGroundTruth || excelGroundTruth.length === 0) return null;
      
      const itemDesc = normalizeText(item.description || item.raw_line || '');
      const itemPartNumber = normalizeText(item.part_number || item.pn_guess || '');
      if (!itemDesc && !itemPartNumber) return null;
      
      // Try to find exact or very close match
      for (const gtRow of excelGroundTruth) {
        const gtDesc = gtRow._description || '';
        if (!gtDesc) continue;
        
        // Check for exact match on description
        if (itemDesc && gtDesc === itemDesc) {
          return gtRow;
        }
        
        // Check for part number match if available
        if (itemPartNumber) {
          // Check various part number fields in ground truth (case-insensitive key lookup)
          const gtPartNumber = normalizeText(
            gtRow['manufacturers.mfr. part number'] || 
            gtRow['manufacturers.mfr part number'] ||
            gtRow['mfr. part number'] ||
            gtRow['mfr part number'] ||
            gtRow['external reference'] || 
            gtRow['part number'] || 
            gtRow['part no'] || 
            gtRow['customer part no.'] || 
            ''
          );
          if (gtPartNumber && gtPartNumber === itemPartNumber) {
            return gtRow;
          }
        }
        
        // Check for substring match (if description is substantial)
        if (itemDesc && itemDesc.length > 10) {
          if (gtDesc.includes(itemDesc) || itemDesc.includes(gtDesc)) {
            // Only consider it a match if significant overlap
            const overlap = itemDesc.length > gtDesc.length 
              ? gtDesc.length / itemDesc.length 
              : itemDesc.length / gtDesc.length;
            if (overlap > 0.7) { // 70% overlap required
              return gtRow;
            }
          }
        }
      }
      
      return null;
    };
  }, [excelGroundTruth]);

  // Filter and sort items: matched items first, unmatched at bottom
  const filteredItems = useMemo(() => {
    let result = items;
    
    // Apply search filter if query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = items.filter(item => {
        // Search across all relevant fields
        const searchableText = [
          item.reference,
          item.description,
          item.raw_line,
          item.manufacturer,
          item.manufacturer_suggestion,
          item.part_number,
          item.pn_guess,
          item.component_type,
          item.type,
          item.item_type,
          item.rating,
          item.qty?.toString(),
          item.quantity?.toString(),
          (item as any).erp_match?.internal_part_number,
          (item as any).erp_match?.external_reference,
        ].filter(Boolean).join(' ').toLowerCase();
        
        return searchableText.includes(query);
      });
    }
    
    // Sort: matched items first, unmatched at bottom
    // Check if ground truth is available for matching
    const hasGroundTruth = excelGroundTruth && excelGroundTruth.length > 0;
    
    if (hasGroundTruth) {
      return result.sort((a, b) => {
        const aMatch = findGroundTruthMatch(a);
        const bMatch = findGroundTruthMatch(b);
        const aHasErp = !!(a as any).erp_match?.internal_part_number;
        const bHasErp = !!(b as any).erp_match?.internal_part_number;
        
        // Priority: 1. Matched items (ground truth or ERP), 2. Unmatched items
        if (aMatch && !bMatch) return -1; // a matched, b not
        if (!aMatch && bMatch) return 1;  // b matched, a not
        if (aHasErp && !bHasErp) return -1; // a has ERP match, b not
        if (!aHasErp && bHasErp) return 1;  // b has ERP match, a not
        
        // Both matched or both unmatched - maintain original order
        return 0;
      });
    }
    
    // If no ground truth, sort by ERP match status
    return result.sort((a, b) => {
      const aHasErp = !!(a as any).erp_match?.internal_part_number;
      const bHasErp = !!(b as any).erp_match?.internal_part_number;
      
      if (aHasErp && !bHasErp) return -1;
      if (!aHasErp && bHasErp) return 1;
      return 0;
    });
  }, [items, searchQuery, excelGroundTruth, findGroundTruthMatch]);

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated items with their original indices
  const paginatedItems = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredItems.slice(startIndex, endIndex).map((item, localIndex) => {
      // Find the original index in the full items array
      const originalIndex = items.findIndex(i => i === item);
      return {
        item,
        originalIndex: originalIndex >= 0 ? originalIndex : startIndex + localIndex,
        filteredIndex: startIndex + localIndex,
      };
    });
  }, [filteredItems, items, page, rowsPerPage]);

  const getConfidenceColor = (confidence: number): 'success' | 'warning' | 'error' => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  // Get the actual item with edits applied
  const getItemWithEdits = (item: BOMItem, rowIndex: number): BOMItem => {
    const edits = editedItems.get(rowIndex);
    return edits ? { ...item, ...edits } : item;
  };

  // Handle cell edit
  const handleCellEdit = (rowIndex: number, field: string, value: any) => {
    const currentEdits = editedItems.get(rowIndex) || {};
    const newEdits = { ...currentEdits, [field]: value };
    setEditedItems(new Map(editedItems).set(rowIndex, newEdits));
    setHasUnsavedChanges(true);
  };

  // Save all edits
  const handleSaveEdits = () => {
    // Create a map of all items with their edits applied
    const updatedItems = items.map((item, index) => {
      // Find if this item has edits in the editedItems map
      // We need to match by item reference or use index
      const edits = editedItems.get(index);
      return edits ? { ...item, ...edits } : item;
    });
    setExtractedItems(updatedItems);
    setEditedItems(new Map());
    setHasUnsavedChanges(false);
    setEditingCell(null);
    showToast('success', 'Changes saved successfully');
  };


  // Helper component for enriched fields
  const EnrichedField: React.FC<{
    value: string | undefined;
    confidence?: number;
  }> = ({ value, confidence }) => {
    if (!value) return <>-</>;

    if (confidence !== undefined) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={`Auto-enriched (${(confidence).toFixed(0)}% match)`}>
            <EnrichedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          </Tooltip>
          <Typography variant="body2">{value}</Typography>
        </Box>
      );
    }

    return <>{value}</>;
  };

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No BOM items extracted yet
      </Typography>
    );
  }

  return (
    <>
      {/* Search and Row count display */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
          <TextField
            size="small"
            placeholder="Search by any column..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <Typography variant="body2" color="text.secondary">
            {filteredItems.length} of {items.length} rows
            {excelGroundTruth && excelGroundTruth.length > 0 && (
              <>
                {' • '}
                <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                  {filteredItems.filter(item => findGroundTruthMatch(item) !== null).length} matched
                </Typography>
                {' • '}
                <Typography component="span" variant="body2" color="warning.main" fontWeight={600}>
                  {filteredItems.filter(item => {
                    const gtMatch = findGroundTruthMatch(item);
                    const hasErp = !!(item as any).erp_match?.internal_part_number;
                    return !gtMatch && !hasErp;
                  }).length} unmatched
                </Typography>
              </>
            )}
          </Typography>
          {hasUnsavedChanges && (
            <Chip 
              label="Unsaved changes" 
              color="warning" 
              size="small"
              icon={<EditIcon />}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Average confidence: {((items.reduce((sum, item) => sum + item.confidence, 0) / items.length) * 100).toFixed(1)}%
          </Typography>
          {hasUnsavedChanges && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                size="small" 
                color="primary" 
                onClick={handleSaveEdits}
                title="Save all changes"
              >
                <SaveIcon />
              </IconButton>
              <IconButton 
                size="small" 
                color="default" 
                onClick={() => {
                  setEditedItems(new Map());
                  setHasUnsavedChanges(false);
                  setEditingCell(null);
                }}
                title="Discard all changes"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => setVerificationReportDialog(true)}
            sx={{ ml: 2 }}
          >
            Complete Verification
          </Button>
        </Box>
      </Box>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'warning.main' }}>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>Level</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>Component</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>Manufacturers.Mfr. Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>Manufacturers.Mfr. Part Number</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>BOM.Qty</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>Part Type</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>Lifecycle Phase</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>Extraction Confidence</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'white' }}>Verification</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedItems.map(({ item, originalIndex, filteredIndex }) => {
              // Use originalIndex for tracking edits (index in full items array)
              const actualItem = getItemWithEdits(item, originalIndex);
              
              // Extract ERP match data
              const erpMatch = (actualItem as any).erp_match;
              
              // Find ground truth match FIRST (it has the authoritative "Number")
              const gtMatch = findGroundTruthMatch(actualItem);
              const isExactMatch = gtMatch !== null;
              
              // Get "Number" from ground truth if matched, otherwise from ERP
              // Ground truth "Number" column = internal part number (e.g., "101037291")
              // ExcelViewer stores headers as lowercase, so "Number" becomes "number"
              let internalPartNumber = '';
              if (gtMatch) {
                // Ground truth has the authoritative "Number" column (stored as lowercase "number")
                const gtNumber = gtMatch['number'] || gtMatch['Number'] || '';
                if (gtNumber && String(gtNumber).trim() !== '') {
                  internalPartNumber = String(gtNumber).trim();
                }
              }
              // Fall back to ERP internal part number if no ground truth match
              if (!internalPartNumber && erpMatch?.internal_part_number) {
                internalPartNumber = erpMatch.internal_part_number;
              } else if (!internalPartNumber && (actualItem as any).internal_part_number) {
                // Last resort: direct field access
                internalPartNumber = (actualItem as any).internal_part_number;
              }
              
              // Get external/manufacturer part number
              // Ground truth "Manufacturers.Mfr. Part Number" = external/manufacturer part number (e.g., "548420")
              // ExcelViewer stores headers as lowercase, so it becomes "manufacturers.mfr. part number"
              let externalPartNumber = '';
              if (gtMatch) {
                // Try various column name variations for manufacturer part number in ground truth
                // ExcelViewer converts headers to lowercase
                const mfrPnCol = gtMatch['manufacturers.mfr. part number'] || 
                                 gtMatch['manufacturers.mfr part number'] ||
                                 gtMatch['mfr. part number'] ||
                                 gtMatch['mfr part number'] ||
                                 gtMatch['Manufacturers.Mfr. Part Number'] ||
                                 gtMatch['part number'] ||
                                 '';
                if (mfrPnCol && String(mfrPnCol).trim() !== '' && String(mfrPnCol).toLowerCase() !== 'nan') {
                  externalPartNumber = String(mfrPnCol).trim();
                }
              }
              // Fall back to ERP or extracted part number
              if (!externalPartNumber) {
                externalPartNumber = erpMatch?.external_reference || 
                                    actualItem.part_number || 
                                    actualItem.pn_guess || 
                                    '';
              }
              
              const hasErpMatch = !!(actualItem as any).erp_match?.internal_part_number;
              const isUnmatched = !isExactMatch && !hasErpMatch;
              
              // Determine level (0 for main items, 1 for sub-items)
              // For now, we'll use 1 for all extracted items (they're all sub-items of the main assembly)
              const level = 1;
              
              // Get part type
              const partType = actualItem.item_type || actualItem.component_type || actualItem.type || '';
              
              const isEditing = editingCell?.rowIndex === originalIndex;
              
              return (
                <TableRow 
                  key={originalIndex} 
                  hover
                  onClick={(e) => {
                    // Don't trigger if clicking on verification buttons or editable cells
                    const target = e.target as HTMLElement;
                    console.log('[BOMTable] Row clicked - target:', target.tagName, target.className);
                    
                    // Only block verification buttons and input fields, allow reference link
                    if (target.closest('.verification-button') || target.closest('input') || isEditing) {
                      console.log('[BOMTable] Blocked: clicked on verification button or input');
                      return;
                    }
                    
                    // Select item and navigate to PDF
                    console.log('[BOMTable] Processing click - selecting item:', actualItem);
                    if (onItemSelect) {
                      onItemSelect(actualItem);
                    }
                    const targetPage = actualItem.pages && actualItem.pages.length > 0 
                      ? actualItem.pages[0] 
                      : actualItem.page || 1;
                    console.log('[BOMTable] Navigating to page:', targetPage);
                    handleNavigateToPage(targetPage);
                  }}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    },
                    // Add border for unmatched items to make them stand out
                    borderLeft: isUnmatched ? '3px solid #ff9800' : 'none',
                  }}
                >
                  <TableCell>{level}</TableCell>
                  <TableCell>
                    {item.reference ? (
                      <Tooltip 
                        title={
                          <Box>
                            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                              <strong>Component:</strong> {item.reference}
                            </Typography>
                            {item.pages && item.pages.length > 0 && (
                              <Box>
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  <strong>Pages:</strong> {item.pages.join(', ')}
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                                  Click to navigate to page {item.pages[0]}
                                </Typography>
                              </Box>
                            )}
                            {item.page && !item.pages && (
                              <Box>
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  <strong>Page:</strong> {item.page}
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                                  Click to navigate to page {item.page}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => {
                            const targetPage = item.pages && item.pages.length > 0 
                              ? item.pages[0] 
                              : item.page || 1;
                            handleNavigateToPage(targetPage);
                          }}
                          sx={{
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {item.reference}
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingCell?.rowIndex === originalIndex && editingCell?.field === 'manufacturer' ? (
                      <TextField
                        size="small"
                        value={actualItem.manufacturer || actualItem.manufacturer_suggestion || ''}
                        onChange={(e) => handleCellEdit(originalIndex, 'manufacturer', e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        autoFocus
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <Box 
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                        onClick={() => setEditingCell({ rowIndex: originalIndex, field: 'manufacturer' })}
                      >
                        <EnrichedField 
                          value={actualItem.manufacturer || actualItem.manufacturer_suggestion} 
                          confidence={actualItem.manufacturer_confidence}
                        />
                        {editedItems.has(originalIndex) && editedItems.get(originalIndex)?.manufacturer && (
                          <EditIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingCell?.rowIndex === originalIndex && editingCell?.field === 'part_number' ? (
                      <TextField
                        size="small"
                        value={externalPartNumber}
                        onChange={(e) => handleCellEdit(originalIndex, 'part_number', e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        autoFocus
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <Box 
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                        onClick={() => setEditingCell({ rowIndex: originalIndex, field: 'part_number' })}
                      >
                        {externalPartNumber ? (
                          <>
                            <Typography variant="body2">{externalPartNumber}</Typography>
                            {isExactMatch && (
                              <Tooltip title="Exact match with ground truth">
                                <Chip 
                                  label="✓" 
                                  size="small" 
                                  color="success"
                                  sx={{ height: 18, fontSize: '0.7rem' }}
                                />
                              </Tooltip>
                            )}
                            {editedItems.has(originalIndex) && editedItems.get(originalIndex)?.part_number && (
                              <EditIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingCell?.rowIndex === originalIndex && editingCell?.field === 'description' ? (
                      <TextField
                        size="small"
                        multiline
                        maxRows={3}
                        value={actualItem.description || actualItem.raw_line || ''}
                        onChange={(e) => handleCellEdit(originalIndex, 'description', e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        autoFocus
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <Box 
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setEditingCell({ rowIndex: originalIndex, field: 'description' })}
                      >
                        <Typography variant="body2">
                          {actualItem.description || actualItem.raw_line || '-'}
                        </Typography>
                        {editedItems.has(originalIndex) && editedItems.get(originalIndex)?.description && (
                          <EditIcon sx={{ fontSize: 14, color: 'primary.main', ml: 0.5 }} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingCell?.rowIndex === originalIndex && editingCell?.field === 'qty' ? (
                      <TextField
                        size="small"
                        type="number"
                        value={actualItem.qty || actualItem.quantity || ''}
                        onChange={(e) => handleCellEdit(originalIndex, 'qty', parseInt(e.target.value) || 0)}
                        onBlur={() => setEditingCell(null)}
                        autoFocus
                        sx={{ width: 80 }}
                      />
                    ) : (
                      <Box 
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                        onClick={() => setEditingCell({ rowIndex: originalIndex, field: 'qty' })}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {actualItem.qty || actualItem.quantity || '-'}
                        </Typography>
                        {editedItems.has(originalIndex) && editedItems.get(originalIndex)?.qty !== undefined && (
                          <EditIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <EnrichedField 
                      value={partType} 
                      confidence={item.type_confidence || item.item_type_confidence}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">Active</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${(actualItem.confidence * 100).toFixed(0)}%`}
                      color={getConfidenceColor(actualItem.confidence)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {(actualItem as any).verification_status === 'verified' ? (
                        <Chip
                          label="✓ Verified"
                          color="success"
                          size="small"
                          variant="filled"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVerifyItem(actualItem, 'verified', e);
                          }}
                          sx={{ cursor: 'pointer' }}
                          title="Click to unverify and show True/False buttons"
                        />
                      ) : (actualItem as any).verification_status === 'incorrect' ? (
                        <Chip
                          label="✗ Incorrect"
                          color="error"
                          size="small"
                          variant="filled"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVerifyItem(actualItem, 'incorrect', e);
                          }}
                          sx={{ cursor: 'pointer' }}
                          title="Click to unmark and show True/False buttons"
                        />
                      ) : (
                        <>
                          <Button
                            className="verification-button"
                            variant={actualItem.verification_status === 'verified' ? 'contained' : 'outlined'}
                            color="success"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerifyItem(actualItem, 'verified', e);
                            }}
                            sx={{ minWidth: 60 }}
                            title="Mark as verified"
                          >
                            True
                          </Button>
                          <Button
                            className="verification-button"
                            variant={actualItem.verification_status === 'incorrect' ? 'contained' : 'outlined'}
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerifyItem(actualItem, 'incorrect', e);
                            }}
                            sx={{ minWidth: 60 }}
                            title="Mark as incorrect"
                          >
                            False
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredItems.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
      />

      {/* Verification Report Dialog */}
      <Dialog 
        open={verificationReportDialog} 
        onClose={() => !isGeneratingReport && setVerificationReportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Complete Verification Report</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will generate a verification report with all verified items, true positives, and false positives.
            You can add notes below before saving.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes (Optional)"
            value={verificationNotes}
            onChange={(e) => setVerificationNotes(e.target.value)}
            placeholder="Add any additional notes or comments about this verification..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationReportDialog(false)} disabled={isGeneratingReport}>
            Cancel
          </Button>
          <Button 
            onClick={handleCompleteVerification} 
            variant="contained" 
            color="primary"
            disabled={isGeneratingReport}
          >
            {isGeneratingReport ? 'Generating...' : 'Generate & Save Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Report Dialog */}
      <Dialog 
        open={emailDialog} 
        onClose={() => !isSendingEmail && setEmailDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Email Verification Report</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The verification report has been saved. Enter an email address to send the report.
          </DialogContentText>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            placeholder="recipient@example.com"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(false)} disabled={isSendingEmail}>
            Skip
          </Button>
          <Button 
            onClick={handleEmailReport} 
            variant="contained" 
            color="primary"
            disabled={isSendingEmail || !emailAddress}
          >
            {isSendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog - PDF-level notes */}
      <Dialog 
        open={notesDialog.open} 
        onClose={() => !isSavingNotes && setNotesDialog({ open: false, notes: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {uploadedPdfFilename ? `Notes for ${uploadedPdfFilename}` : 'Add Notes'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={8}
            label="Extraction Notes"
            value={notesDialog.notes}
            onChange={(e) => setNotesDialog({ ...notesDialog, notes: e.target.value })}
            placeholder="Add notes about this PDF extraction..."
            disabled={isSavingNotes}
            sx={{ mt: 1 }}
            helperText="These notes are associated with the entire PDF extraction, not individual items."
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setNotesDialog({ open: false, notes: '' })} 
            disabled={isSavingNotes}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!uploadedPdfFilename) {
                showToast('error', 'No PDF file selected');
                return;
              }
              
              setIsSavingNotes(true);
              try {
                await bomApi.updateJobNotes(uploadedPdfFilename, notesDialog.notes || null);
                
                // Update local state
                setJobNotes(notesDialog.notes || null);
                
                showToast('success', 'Notes saved successfully');
                setNotesDialog({ open: false, notes: '' });
              } catch (error: any) {
                showToast('error', `Failed to save notes: ${error.message}`);
              } finally {
                setIsSavingNotes(false);
              }
            }}
            variant="contained"
            color="primary"
            disabled={isSavingNotes}
          >
            {isSavingNotes ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BOMTable;
